// Host page: camera -> canvas (stamp + Baghdad clock) -> mediasoup producer.
// See SPEC.md "host.html".
import {
  Lab, getKey, requireKeyOrExplain, loadDevice, selectedPairInfo, detectPlatform,
  submitResult, fmtMs, carrierPicker, keepScreenOn, pathLabel,
} from '../common.js';
import { drawStamp } from '../stamp.js';

const $ = (id) => document.getElementById(id);

const SIMULCAST = [
  { rid: 'r0', scaleResolutionDownBy: 4, maxBitrate: 150_000 },
  { rid: 'r1', scaleResolutionDownBy: 2, maxBitrate: 500_000 },
  { rid: 'r2', maxBitrate: 1_500_000 },
];
const SINGLE = [{ maxBitrate: 1_200_000 }];
const BAGHDAD_OFFSET_MS = 3 * 3600 * 1000;
// The media path (ICE + DTLS) must come up this fast, or the network blocks it.
const MEDIA_CONNECT_TIMEOUT_MS = 15_000;
// A camera captureTime older than this is not trusted (stalled camera or odd clock domain).
const MAX_CAPTURE_LAG_MS = 1000;
const FAMILIES = ['ipv4', 'ipv6'];

// family: the family the WebRTC path used; preferred: the family the room's worker ranks first.
const S = { state: 'idle', roomId: null, codec: null, simulcast: false, viewers: 0, family: null, preferred: null, error: null };
Object.defineProperty(window, '__lab', {
  configurable: false,
  enumerable: true,
  get: () => ({ ...S }),
});

let picker = null;
let lab = null;
let clock = null;
let device = null;
let sendTransport = null;
let videoProducer = null;
let audioProducer = null;
let camStream = null;
let camVideo = null;
let canvas = null;
let ctx = null;
let outTrack = null;
let rafId = 0;
let lastDraw = 0;
let statsTimer = 0;
let durationTimer = 0;
let liveSince = 0;
let unsubs = [];
let stopping = false;
let wake = null;

// Stamp source: the camera capture time of the frame being drawn (rVFC captureTime), so
// the viewer's delay includes the host camera pipeline, like the manual TikTok method.
let lastCapPerf = null;
const stamping = { capture: 0, draw: 0, lagSum: 0, lagN: 0 };

// Time the page was hidden while live: rAF stops, so viewers see a frozen frame.
const pause = { hiddenSince: 0, totalMs: 0, count: 0 };

const stats = {
  lastBytes: 0,
  lastAt: 0,
  totalBytes: 0,
  layerBytes: new Map(),
  protocol: null,
  family: null,
  rttMs: null,
};

// ---------- helpers ----------

const r1 = (x) => (Number.isFinite(x) ? Math.round(x * 10) / 10 : undefined);
const p2 = (n) => String(n).padStart(2, '0');
// Keeps Latin runs such as "1280×720" in visual order inside RTL text.
const ltr = (s) => `⁦${s}⁩`;
const p3 = (n) => String(n).padStart(3, '0');

function clean(v) {
  if (Array.isArray(v)) return v.map(clean);
  if (v && typeof v === 'object') {
    const out = {};
    for (const [k, x] of Object.entries(v)) {
      if (x === undefined || x === null) continue;
      if (typeof x === 'number' && !Number.isFinite(x)) continue;
      out[k] = clean(x);
    }
    return out;
  }
  return v;
}

function setText(id, text) {
  const el = $(id);
  if (el) el.textContent = text;
}

function show(el, on = true) {
  if (el) el.classList.toggle('hidden', !on);
}

function showError(msg) {
  const el = $('error');
  el.textContent = msg;
  show(el, true);
}

function setStatus(msg) {
  const el = $('status');
  el.textContent = msg || '';
  show(el, !!msg);
}

function baghdadClock(ms) {
  const d = new Date(ms + BAGHDAD_OFFSET_MS);
  return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}.${p3(d.getUTCMilliseconds())}`;
}

function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${p2(Math.floor(s / 60))}:${p2(s % 60)}`;
}

function codecName(mimeType) {
  if (!mimeType) return null;
  const n = mimeType.split('/')[1] || mimeType;
  return n.toUpperCase() === 'H264' ? 'H264' : n.toUpperCase();
}

function explainError(err) {
  const name = err && err.name;
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return 'لم يُسمح باستعمال الكاميرا أو الميكروفون. اسمح بهما من إعدادات المتصفّح ثمّ أعد المحاولة.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'لم نجد كاميرا أو ميكروفوناً على هذا الجهاز.';
  if (name === 'NotReadableError' || name === 'TrackStartError') return 'الكاميرا مشغولة بتطبيق آخر. أغلقه ثمّ أعد المحاولة.';
  if (name === 'UnsupportedError') return 'متصفّحك لا يدعم البثّ عبر WebRTC. استعمل Chrome أو Safari بإصدار حديث.';
  switch (err && err.message) {
    case 'unsupported':
      return 'متصفّحك لا يدعم البثّ عبر WebRTC. استعمل Chrome أو Safari بإصدار حديث.';
    case 'no_key':
      return 'الرابط ناقص. اطلب رابط التجربة من الإدارة.';
    case 'no_camera_frames':
      return 'لم تصل صورة من الكاميرا. أعد المحاولة.';
    case 'unauthorized':
      return 'الرابط غير صالح. اطلب رابط التجربة من الإدارة.';
    case 'too_many_rooms':
      return 'الخادم مشغول بعدد كبير من البثوث. حاول بعد قليل.';
    case 'rate_limited':
      return 'طلبات كثيرة في وقت قصير. انتظر دقيقة ثمّ أعد المحاولة.';
    case 'timeout':
      return 'لم يردّ الخادم في الوقت المناسب. تحقّق من الاتّصال ثمّ أعد المحاولة.';
    case 'media_blocked':
      return 'شبكتك تمنع اتّصال البثّ. جرّب شبكة أخرى أو واي فاي.';
    case 'transport_failed':
      return 'انقطع اتّصال البثّ بالخادم.';
    default:
      return 'انقطع الاتّصال بالخادم. تحقّق من الإنترنت ثمّ أعد المحاولة.';
  }
}

function roomClosedText(reason) {
  switch (reason) {
    case 'ttl':
      return 'أغلق الخادم البثّ لأنّ مدّته انتهت.';
    case 'idle':
      return 'أغلق الخادم البثّ لأنّ الصورة لم تصل إليه مدّة طويلة.';
    case 'shutdown':
      return 'أُعيد تشغيل الخادم، فانتهى البثّ.';
    default:
      return 'أغلق الخادم البثّ.';
  }
}

function supportsWebRTC() {
  return typeof RTCPeerConnection !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof HTMLCanvasElement !== 'undefined'
    && typeof HTMLCanvasElement.prototype.captureStream === 'function';
}

// Thrown when stop() ran while start() was still awaiting.
class Aborted extends Error {}

function ensureStarting() {
  if (S.state !== 'starting') throw new Aborted('aborted');
}

// ---------- camera + compositor ----------

async function openCamera(facingMode) {
  const video = { facingMode, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: true, video });
  } catch (e) {
    if (e && (e.name === 'OverconstrainedError' || e.name === 'ConstraintNotSatisfiedError')) {
      return navigator.mediaDevices.getUserMedia({ audio: true, video: { facingMode } });
    }
    throw e;
  }
}

function waitForFrames(video, ms) {
  return new Promise((resolve, reject) => {
    const t0 = performance.now();
    const check = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) return resolve();
      if (performance.now() - t0 > ms) return reject(new Error('no_camera_frames'));
      setTimeout(check, 50);
    };
    check();
  });
}

function watchCaptureTimes(video) {
  if (typeof video.requestVideoFrameCallback !== 'function') return;
  const onFrame = (now, meta) => {
    if (camVideo !== video) return;
    const ct = meta && meta.captureTime;
    const lag = performance.now() - ct;
    lastCapPerf = Number.isFinite(ct) && lag >= 0 && lag < MAX_CAPTURE_LAG_MS ? ct : null;
    video.requestVideoFrameCallback(onFrame);
  };
  video.requestVideoFrameCallback(onFrame);
}

async function startCompositor(videoTrack) {
  camVideo = document.createElement('video');
  camVideo.muted = true;
  camVideo.playsInline = true;
  camVideo.autoplay = true;
  camVideo.setAttribute('playsinline', '');
  camVideo.setAttribute('muted', '');
  // Must stay in the DOM (iOS stops decoding detached videos); kept tiny and invisible.
  Object.assign(camVideo.style, {
    position: 'fixed', left: '0', top: '0', width: '2px', height: '2px',
    opacity: '0.01', pointerEvents: 'none', zIndex: '-1',
  });
  document.body.appendChild(camVideo);
  camVideo.srcObject = new MediaStream([videoTrack]);
  watchCaptureTimes(camVideo);
  await camVideo.play().catch(() => {});
  await waitForFrames(camVideo, 8000);

  canvas = document.createElement('canvas');
  canvas.width = camVideo.videoWidth;
  canvas.height = camVideo.videoHeight;
  $('preview-box').replaceChildren(canvas);
  ctx = canvas.getContext('2d', { alpha: false });

  drawFrame();
  const stream = canvas.captureStream(30);
  outTrack = stream.getVideoTracks()[0];
  if (!outTrack) throw new Error('unsupported');
}

function drawClockLabel(w, h, serverMs) {
  const fs = Math.max(14, Math.round(Math.min(w, h) / 14));
  ctx.font = `700 ${fs}px ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace`;
  try { ctx.direction = 'ltr'; } catch { /* older engines */ }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const text = baghdadClock(serverMs);
  const tw = ctx.measureText(text).width;
  const pad = Math.round(fs * 0.35);
  const x = Math.round((w - tw) / 2);
  const y = Math.round(h - fs * 0.6);
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(x - pad, y - fs - pad * 0.2, tw + pad * 2, fs + pad * 1.4);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, x, y);
}

function drawFrame() {
  rafId = requestAnimationFrame(drawFrame);
  const t = performance.now();
  if (t - lastDraw < 12) return; // cap at ~60 Hz on high-refresh screens
  lastDraw = t;
  const vw = camVideo?.videoWidth;
  const vh = camVideo?.videoHeight;
  if (!vw || !vh || !ctx) return;
  if (canvas.width !== vw || canvas.height !== vh) {
    canvas.width = vw;
    canvas.height = vh;
  }
  ctx.drawImage(camVideo, 0, 0, vw, vh);
  const drawNow = clock.now();
  // Camera capture time when the engine reports it (Chrome), else the draw time (Safari).
  let stampMs = drawNow;
  const cap = lastCapPerf != null ? clock.toServer(lastCapPerf) : NaN;
  const lag = drawNow - cap;
  if (Number.isFinite(lag) && lag >= 0 && lag < MAX_CAPTURE_LAG_MS) {
    stampMs = cap;
    if (S.state === 'live') {
      stamping.capture++;
      stamping.lagSum += lag;
      stamping.lagN++;
    }
  } else if (S.state === 'live') {
    stamping.draw++;
  }
  drawStamp(ctx, vw, vh, stampMs);
  drawClockLabel(vw, vh, stampMs);
}

// ---------- mediasoup ----------

function pickCodec(pref) {
  let caps;
  try { caps = device.sendRtpCapabilities; } catch { caps = null; }
  const codecs = ((caps && caps.codecs) || device.rtpCapabilities.codecs || [])
    .filter((c) => c.kind === 'video');
  const want = pref === 'auto' ? (detectPlatform().os === 'ios' ? 'h264' : 'vp8') : pref;
  const mime = want === 'h264' ? 'video/h264' : 'video/vp8';
  const list = codecs.filter((c) => String(c.mimeType).toLowerCase() === mime);
  if (want === 'h264') {
    return list.find((c) => c.parameters?.['profile-level-id'] === '42e01f'
      && Number(c.parameters?.['packetization-mode']) === 1) ?? list[0] ?? null;
  }
  return list[0] ?? null;
}

function wireSendTransport(transport) {
  transport.on('connect', ({ dtlsParameters }, callback, errback) => {
    lab.request('transport.connect', { transportId: transport.id, dtlsParameters })
      .then(() => callback(), errback);
  });
  transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
    const d = { transportId: transport.id, kind, rtpParameters };
    if (appData && Object.keys(appData).length) d.appData = appData;
    lab.request('produce', d, 10000).then(({ id }) => callback({ id }), errback);
  });
  transport.on('connectionstatechange', (st) => {
    if (st === 'failed' && S.state === 'live') {
      showError(explainError(new Error('transport_failed')));
      stop('error');
    } else if (st === 'disconnected' && S.state === 'live') {
      setStatus('الاتّصال ضعيف… نحاول الاستمرار.');
    } else if (st === 'connected' && S.state === 'live') {
      setStatus('');
    }
  });
}

// Resolves once ICE + DTLS are up. produce() only needs signaling, so without this the page
// would show «على الهواء» while no media can flow (UDP and ICE-TCP both blocked).
function waitMediaConnected(transport, ms) {
  return new Promise((resolve, reject) => {
    let done = false;
    const finish = (err) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      clearInterval(poll);
      transport.removeListener('connectionstatechange', onState);
      if (err) reject(err);
      else resolve();
    };
    const onState = (st) => {
      if (st === 'connected') finish();
      else if (st === 'failed' || st === 'closed') finish(new Error('media_blocked'));
    };
    const timer = setTimeout(() => finish(new Error('media_blocked')), ms);
    // stop() may tear the transport down meanwhile.
    const poll = setInterval(() => {
      if (S.state !== 'starting' || transport.closed) finish(new Aborted('aborted'));
    }, 200);
    transport.on('connectionstatechange', onState);
    if (transport.connectionState === 'connected') finish();
  });
}

async function produceVideo(codec) {
  const base = { track: outTrack, codecOptions: { videoGoogleStartBitrate: 1000 }, stopTracks: false };
  if (codec) base.codec = codec;
  try {
    const p = await sendTransport.produce({ ...base, encodings: SIMULCAST });
    return { producer: p, simulcast: (p.rtpParameters?.encodings?.length || 0) > 1, fellBack: false };
  } catch (e) {
    if (S.state !== 'starting') throw e;
    const p = await sendTransport.produce({ ...base, encodings: SINGLE });
    return { producer: p, simulcast: false, fellBack: true };
  }
}

// ---------- live stats ----------

async function tickStats() {
  if (S.state !== 'live' || !sendTransport) return;
  let report;
  try { report = await sendTransport.getStats(); } catch { return; }
  const now = performance.now();
  let bytes = 0;
  let layers = 0;
  let topW = 0;
  let topH = 0;
  let limit = null;
  report.forEach((s) => {
    if (s.type !== 'outbound-rtp') return;
    bytes += s.bytesSent || 0;
    const kind = s.kind || s.mediaType;
    if (kind !== 'video') return;
    const prev = stats.layerBytes.get(s.ssrc) ?? 0;
    if ((s.bytesSent || 0) > prev) layers++;
    stats.layerBytes.set(s.ssrc, s.bytesSent || 0);
    if ((s.frameWidth || 0) > topW) {
      topW = s.frameWidth;
      topH = s.frameHeight || 0;
      if (s.qualityLimitationReason) limit = s.qualityLimitationReason;
    }
  });
  if (stats.lastAt && now > stats.lastAt && bytes >= stats.lastBytes) {
    const kbps = ((bytes - stats.lastBytes) * 8) / (now - stats.lastAt);
    setText('s-kbps', `${Math.round(kbps)} kbps`);
  }
  stats.lastBytes = bytes;
  stats.lastAt = now;
  stats.totalBytes = Math.max(stats.totalBytes, bytes);

  const src = canvas ? ltr(`${canvas.width}×${canvas.height}`) : '—';
  setText('s-res', topW ? `${src} (المُرسَل: ${ltr(`${topW}×${topH}`)})` : src);
  setText('s-layers', S.simulcast ? `${layers} / 3` : String(layers || 1));
  const limits = { none: 'لا يوجد', bandwidth: 'سرعة الشبكة', cpu: 'قدرة المعالج', other: 'أخرى' };
  setText('s-limit', limit ? (limits[limit] || limit) : '—');

  const pair = await selectedPairInfo(sendTransport).catch(() => null);
  if (pair) {
    if (pair.protocol) stats.protocol = pair.protocol;
    if (pair.family) stats.family = S.family = pair.family;
    if (Number.isFinite(pair.rttMs)) stats.rttMs = pair.rttMs;
  }
  setText('s-proto', pathLabel(stats.protocol, stats.family));
  const rtt = Number.isFinite(stats.rttMs) ? stats.rttMs : (clock ? clock.uncertaintyMs * 2 : NaN);
  setText('s-rtt', Number.isFinite(rtt) ? fmtMs(rtt) : '—');
}

// ---------- start / stop ----------

function pausedMsNow() {
  return pause.totalMs + (pause.hiddenSince ? performance.now() - pause.hiddenSince : 0);
}

function buildPayload() {
  const platform = detectPlatform();
  const liveS = liveSince ? (performance.now() - liveSince) / 1000 : 0;
  const stamped = stamping.capture + stamping.draw;
  return clean({
    kind: 'host',
    carrier: picker?.value,
    note: '',
    platform: { os: platform.os, browser: platform.browser },
    effectiveType: navigator.connection?.effectiveType,
    clockUncertaintyMs: r1(clock?.uncertaintyMs),
    durationS: r1(liveS),
    roomId: S.roomId,
    codec: S.codec,
    simulcast: !!S.simulcast,
    outKbpsAvg: liveS > 0 ? r1((stats.totalBytes * 8) / liveS / 1000) : undefined,
    protocol: stats.protocol,
    family: stats.family,
    preferred: S.preferred,
    width: canvas?.width,
    height: canvas?.height,
    stampSource: stamped ? (stamping.capture >= stamping.draw ? 'capture' : 'draw') : undefined,
    captureLagMs: stamping.lagN ? r1(stamping.lagSum / stamping.lagN) : undefined,
    pausedMs: Math.round(pausedMsNow()),
    pauses: pause.count + (pause.hiddenSince ? 1 : 0),
  });
}

async function copyWatchLink(full) {
  let ok = false;
  try {
    await navigator.clipboard.writeText(full);
    ok = true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = full;
    ta.setAttribute('readonly', '');
    Object.assign(ta.style, { position: 'fixed', top: '0', left: '0', opacity: '0' });
    document.body.appendChild(ta);
    ta.select();
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    ta.remove();
  }
  setText('copy-status', ok ? 'نُسخ الرابط ✓ (يتضمّن مفتاح التجربة)' : 'تعذّر النسخ. انسخ الرابط يدويّاً.');
}

function setWatchLink(roomId) {
  const base = `${location.origin}/watch.html?r=${encodeURIComponent(roomId)}`;
  const key = getKey() || '';
  const full = `${base}&k=${encodeURIComponent(key)}`;
  const a = $('watch-link');
  a.href = full;
  a.textContent = base;
  // Opening the link here would hide this page and freeze the broadcast: copy it instead.
  a.onclick = (ev) => {
    ev.preventDefault();
    copyWatchLink(full);
  };

  $('copy').onclick = () => copyWatchLink(full);

  const shareBtn = $('share');
  if (typeof navigator.share === 'function') {
    show(shareBtn, true);
    shareBtn.onclick = () => {
      navigator.share({ title: 'مشاهدة بثّ تجريبيّ', url: full }).catch(() => {});
    };
  }
}

async function start() {
  if (S.state === 'starting' || S.state === 'live') return;
  show($('error'), false);
  const carrier = picker?.value;
  if (!carrier) {
    showError('اختر شبكتك أوّلاً.');
    return;
  }
  if (!supportsWebRTC()) {
    S.state = 'error';
    S.error = 'unsupported';
    showError(explainError(new Error('unsupported')));
    return;
  }

  S.state = 'starting';
  S.error = null;
  stopping = false;
  liveSince = 0;
  lastCapPerf = null;
  Object.assign(stamping, { capture: 0, draw: 0, lagSum: 0, lagN: 0 });
  Object.assign(pause, { hiddenSince: 0, totalMs: 0, count: 0 });
  Object.assign(stats, { lastBytes: 0, lastAt: 0, totalBytes: 0, protocol: null, family: null, rttMs: null });
  S.family = null;
  S.preferred = null;
  stats.layerBytes.clear();
  // Before the first await: the wake lock request belongs to this tap. The draw loop runs on
  // requestAnimationFrame, which stops when the screen turns off.
  wake = keepScreenOn();
  const startBtn = $('start');
  startBtn.disabled = true;
  setStatus('نطلب إذن الكاميرا والميكروفون…');

  try {
    camStream = await openCamera($('facing').value);
    ensureStarting();
    const vTrack = camStream.getVideoTracks()[0];
    const aTrack = camStream.getAudioTracks()[0] || null;
    if (!vTrack) throw Object.assign(new Error('no camera'), { name: 'NotFoundError' });

    setStatus('نتّصل بالخادم…');
    lab = await Lab.connect();
    ensureStarting();
    unsubs.push(lab.on('room.viewers', (d) => {
      S.viewers = Number(d?.viewers) || 0;
      setText('s-viewers', String(S.viewers));
    }));
    unsubs.push(lab.on('room.closed', (d) => {
      if (S.state === 'live' || S.state === 'starting') {
        showError(roomClosedText(d?.reason));
        stop('closed');
      }
    }));
    unsubs.push(lab.on('close', () => {
      if (S.state === 'live' || S.state === 'starting') {
        showError(explainError(new Error('connection_lost')));
        stop('error');
      }
    }));
    clock = lab.clock;
    await clock.syncInitial(15);
    ensureStarting();
    clock.start();

    setStatus('نجهّز الصورة…');
    await startCompositor(vTrack);
    ensureStarting();

    setStatus('ننشئ البثّ…');
    const { roomId } = await lab.request('room.create', {});
    S.roomId = roomId;
    ensureStarting();
    const join = await lab.request('room.join', { roomId, role: 'host' });
    S.preferred = FAMILIES.includes(join.preferredFamily) ? join.preferredFamily : null;
    device = await loadDevice(join.routerRtpCapabilities);
    ensureStarting();
    if (!device.canProduce('video')) throw new Error('unsupported');

    const tp = await lab.request('transport.create', { direction: 'send' });
    ensureStarting();
    sendTransport = device.createSendTransport(tp);
    wireSendTransport(sendTransport);

    const pref = $('codec').value;
    const wanted = pickCodec(pref);
    const v = await produceVideo(wanted);
    ensureStarting();
    videoProducer = v.producer;
    S.simulcast = v.simulcast;
    S.codec = codecName(videoProducer.rtpParameters?.codecs?.[0]?.mimeType) || codecName(wanted?.mimeType);

    if (aTrack && device.canProduce('audio')) {
      try {
        audioProducer = await sendTransport.produce({ track: aTrack, stopTracks: false });
      } catch { audioProducer = null; }
    }
    ensureStarting();

    setStatus('نتّصل بخادم البثّ…');
    await waitMediaConnected(sendTransport, MEDIA_CONNECT_TIMEOUT_MS);
    ensureStarting();

    S.state = 'live';
    liveSince = performance.now();
    if (document.visibilityState === 'hidden') pause.hiddenSince = liveSince;
    setStatus('');
    show($('setup'), false);
    show(startBtn, false);
    show($('live'), true);
    setWatchLink(roomId);

    setText('s-codec', S.codec || '—');
    const sb = $('simulcast-badge');
    sb.textContent = S.simulcast ? '3 طبقات' : 'طبقة واحدة';
    sb.classList.toggle('warn', !S.simulcast);
    const notes = [];
    if (v.fellBack) notes.push('فشل البثّ متعدّد الطبقات، فاستُعملت طبقة واحدة.');
    if (pref !== 'auto' && !wanted) notes.push('الترميز المطلوب غير مدعوم هنا، فاستُعمل ترميز آخر.');
    if (!audioProducer) notes.push('يعمل البثّ بلا صوت.');
    setStatus(notes.join(' '));

    statsTimer = setInterval(tickStats, 1000);
    durationTimer = setInterval(() => {
      setText('duration', fmtDuration((performance.now() - liveSince) / 1000));
    }, 1000);
  } catch (e) {
    // stop() already ran and showed its own message; close what was created after it.
    if (e instanceof Aborted || stopping) {
      teardown();
      return;
    }
    S.error = (e && (e.name && e.name !== 'Error' ? e.name : e.message)) || 'error';
    const msg = explainError(e);
    teardown();
    S.state = 'error';
    setStatus('');
    showError(msg);
    startBtn.disabled = false;
    show(startBtn, true);
    show($('setup'), true);
  }
}

function teardown() {
  cancelAnimationFrame(rafId);
  rafId = 0;
  clearInterval(statsTimer);
  clearInterval(durationTimer);
  statsTimer = 0;
  durationTimer = 0;
  for (const u of unsubs) { try { u?.(); } catch { /* ignore */ } }
  unsubs = [];
  try { videoProducer?.close(); } catch { /* ignore */ }
  try { audioProducer?.close(); } catch { /* ignore */ }
  try { sendTransport?.close(); } catch { /* ignore */ }
  videoProducer = null;
  audioProducer = null;
  sendTransport = null;
  try { clock?.stop(); } catch { /* ignore */ }
  try { lab?.close(); } catch { /* ignore */ }
  lab = null;
  try { outTrack?.stop(); } catch { /* ignore */ }
  outTrack = null;
  if (camStream) for (const t of camStream.getTracks()) { try { t.stop(); } catch { /* ignore */ } }
  camStream = null;
  if (camVideo) {
    camVideo.srcObject = null;
    camVideo.remove();
    camVideo = null;
  }
  lastCapPerf = null;
  try { wake?.release(); } catch { /* ignore */ }
  wake = null;
}

function stop(reason) {
  if (stopping) return;
  if (S.state !== 'live' && S.state !== 'starting') return;
  stopping = true;
  const wasLive = S.state === 'live';
  const payload = wasLive ? buildPayload() : null;
  teardown();
  S.state = reason === 'error' ? 'error' : 'stopped';
  if (reason === 'error' && !S.error) S.error = 'connection_lost';
  S.viewers = 0;
  show($('live'), false);
  setStatus('');
  if (!wasLive) {
    // Stopped before going live: allow another try.
    const startBtn = $('start');
    startBtn.disabled = false;
    show(startBtn, true);
    show($('setup'), true);
  }

  if (!payload) return;
  if (reason === 'pagehide') {
    // submitResult starts a keepalive fetch synchronously, so it survives the unload.
    submitResult('host', payload).catch(() => {});
    return;
  }
  setText('f-duration', fmtDuration(payload.durationS || 0));
  setText('f-codec', payload.codec || '—');
  setText('f-simulcast', payload.simulcast ? 'نعم' : 'لا');
  setText('f-kbps', Number.isFinite(payload.outKbpsAvg) ? `${Math.round(payload.outKbpsAvg)} kbps` : '—');
  setText('f-proto', pathLabel(payload.protocol, payload.family));
  setText('f-paused', payload.pausedMs >= 1000 ? fmtDuration(payload.pausedMs / 1000) : 'لا');
  show($('summary'), true);
  setText('submit-status', 'جارٍ إرسال النتيجة…');
  submitResult('host', payload).then(
    () => setText('submit-status', 'أُرسلت النتيجة ✓'),
    () => setText('submit-status', 'تعذّر إرسال النتيجة.'),
  );
}

function onVisibilityChange() {
  if (S.state !== 'live') return;
  const now = performance.now();
  if (document.visibilityState === 'hidden') {
    if (!pause.hiddenSince) pause.hiddenSince = now;
  } else if (pause.hiddenSince) {
    pause.totalMs += now - pause.hiddenSince;
    pause.count++;
    pause.hiddenSince = 0;
    setStatus('توقّف البثّ أثناء مغادرتك الصفحة. أبقِ هذه الصفحة ظاهرة حتّى تنهي البثّ.');
  }
}

function init() {
  const gate = $('gate');
  if (!requireKeyOrExplain(gate)) {
    show(gate, true);
    show($('app'), false);
    return;
  }
  picker = carrierPicker($('carrier'), () => show($('error'), false));
  $('codec').value = 'auto';
  if (typeof navigator.wakeLock?.request !== 'function') show($('awake-note'), true);
  $('start').addEventListener('click', start);
  $('stop').addEventListener('click', () => stop('user'));
  $('again').addEventListener('click', () => location.reload());
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', () => stop('pagehide'));
}

init();
